/*
  Ian - 2026-09-28 - Desktop mega menu hover: open promptly, and don't lose the menu on
  the way down to it.

  What was wrong, measured on the live site (1440px wide) before this was written:

  1) OPENING. Envy opens a dropdown through the `hoverintent` library (assets/theme.js,
     Menu.js) with interval 100ms and sensitivity 7px: the pointer has to move less than
     7px in a 100ms window before the menu shows. Glide onto "Women" without stopping and
     nothing opens, which is the "delay in the menu opening" Lucinda reported (Roadmap
     Catch-up Agenda, 31 Aug 2026, point 5).

  2) CLOSING. The heading row sits at y 157-195 and the dropdown starts at y 215. Envy's own
     10px invisible strip under each heading covers 195-205, which leaves 205-215 outside the
     trigger, plus the 22px between headings. Crossing either fires `mouseleave` on the
     trigger and Envy hides the menu 150ms later. Confirmed on live: open Women, pause in the
     gap under it or under Men, and the menu closes. TBCo (the reference Ian gave) has no
     such gap: its dropdown starts on the line its heading row ends.

  What this does, mega menu headings only, desktop pointer only (min-width 1024px,
  hover-capable, not the theme editor):

  a) Measures the real gap between the heading row and the dropdown and writes it to
     `--ian-nav-bridge` on #AccessibleNav. `assets/ian-custom.css` uses it to make the
     heading link's invisible ::after strip cover the whole gap and the space between
     headings, so the pointer never leaves the trigger on its way into the dropdown (the
     dropdown is a child of the trigger, so Envy's `mouseleave` never fires).

  b) Opens a menu after 60ms over its heading text when nothing is open, so a glide onto
     "Women" opens it. Envy's hoverintent still runs alongside and still opens a neighbour
     when the pointer settles on the neighbour's heading text (about 100ms).

  c) Keeps the open menu when the pointer pauses in the strip under a NEIGHBOURING heading,
     which is where a diagonal path from one heading into its menu often passes. Envy's
     hoverintent treats the strip as part of the neighbour and would open it; if that
     happens while the pointer is outside the neighbour's heading text, the previous menu is
     put straight back (microtask, so before paint). Once the pointer moves up onto that
     neighbour's heading text and settles for 120ms, it opens as normal. Keyboard focus
     opens menus through Envy untouched, because this only steps in while the pointer is
     physically over the neighbour.

  d) If Envy's 150ms hide timer fires while the pointer is still on a mega menu heading, its
     strip or inside the dropdown, the menu is put straight back (microtask, after Envy's
     own listener, before paint, so no blink).

  e) Ignores a click that lands on the strip rather than on the heading text, so a click in
     the gap never takes the shopper to the heading's collection page. Keyboard activation
     (Enter, which reports detail 0) is untouched.

  f) Added 29 Sep 2026 after Ian tested the draft: hover Women, go diagonally down and to the
     left, and the menu still closed. The path crosses the gap under New In, a plain link with
     no dropdown and so no strip of its own, and Women's `mouseleave` fired as before. Now a
     full-width band across the gap (`#AccessibleNav::after` in ian-custom.css) counts as the
     menu bar, and anywhere on the bar (headings, the spaces between them, the band, an open
     dropdown) keeps the open menu. To keep New In usable, settling on New In's own text for
     120ms closes the open menu, the same test used for switching between headings. Leaving
     the bar altogether closes it 150ms later, matching Envy's own delay.

  Envy's own handlers stay in place. Opening goes through the same `mega-menu:show` event
  and `nav-hover` class Envy uses, so the fade-in, the sibling close and the touch/keyboard
  paths are unchanged. Nothing here runs on touch devices or below 1024px.
*/
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;
  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var OPEN_DELAY = 60;      // ms over a heading before it opens, when no menu is open
  var SWITCH_DELAY = 120;   // ms settled over a neighbour's heading before it takes over (fallback to Envy's own)
  var SWITCH_TRAVEL = 10;   // px the pointer may drift in that window and still count as settled
  var TICK = 40;            // ms between settle checks
  var DESKTOP = 1024;       // Envy's own desktop threshold in Menu.js

  function isDesktop() {
    return window.innerWidth >= DESKTOP;
  }

  function init() {
    var nav = document.getElementById('AccessibleNav');
    var bus = window.eventBus;
    if (!nav || !bus || typeof bus.on !== 'function' || typeof bus.emit !== 'function') return false;

    var items = Array.prototype.slice.call(nav.children).filter(function (li) {
      return li.tagName === 'LI';
    });
    var triggers = items.filter(function (li) {
      return li.classList.contains('site-nav--has-dropdown') && li.classList.contains('mega-menu');
    });
    var plain = items.filter(function (li) {
      return !li.classList.contains('site-nav--has-dropdown');
    });
    if (!triggers.length) return true;
    var header = nav.closest('.site-header');

    var lastShown = null;   // the trigger whose menu is (or was just) open
    var armed = null;       // trigger the pointer is currently over, awaiting open
    var timer = null;
    var armedAt = 0;
    var reverting = false;
    var history = [];       // recent pointer positions, {x, y, t}

    function linkOf(li) {
      return li.querySelector(':scope > a.site-nav__link');
    }

    function dropdownOf(li) {
      return li.querySelector(':scope > .site-nav__dropdown');
    }

    function triggerFor(dropdownId) {
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].getAttribute('data-mega-menu-trigger-id') === dropdownId) return triggers[i];
      }
      return null;
    }

    function openTrigger() {
      for (var i = 0; i < triggers.length; i++) {
        var dd = dropdownOf(triggers[i]);
        if (dd && dd.classList.contains('show')) return triggers[i];
      }
      return null;
    }

    function hovered(el) {
      try { return !!el && el.matches(':hover'); } catch (e) { return false; }
    }

    function onBar() {
      // any heading, the gaps between them, the full-width band, or an open dropdown
      return hovered(nav);
    }

    function closeAll() {
      disarm();
      lastShown = null;
      items.forEach(function (li) { li.classList.remove('nav-hover'); });
      bus.emit('mega-menu:hide');
    }

    // --- a) strip height ---------------------------------------------------------------

    function measureBridge() {
      if (!isDesktop()) {
        nav.style.removeProperty('--ian-nav-bridge');
        nav.style.removeProperty('--ian-nav-band-top');
        nav.style.removeProperty('--ian-nav-band-height');
        return;
      }
      for (var i = 0; i < triggers.length; i++) {
        var a = linkOf(triggers[i]);
        var dd = dropdownOf(triggers[i]);
        if (!a || !dd) continue;
        var ddRect = dd.getBoundingClientRect();
        if (ddRect.height < 1) continue; // an empty mega menu block is display:none
        var linkBottom = a.getBoundingClientRect().bottom;
        var gap = ddRect.top - linkBottom;
        if (gap > 0 && gap < 120) {
          nav.style.setProperty('--ian-nav-bridge', Math.ceil(gap + 1) + 'px');
          if (header) {
            nav.style.setProperty('--ian-nav-band-top', Math.floor(linkBottom - header.getBoundingClientRect().top) + 'px');
            nav.style.setProperty('--ian-nav-band-height', Math.ceil(gap + 1) + 'px');
          }
        }
        return;
      }
    }

    // --- open / show -----------------------------------------------------------------

    function show(li) {
      if (!li) return;
      triggers.forEach(function (other) {
        if (other !== li) other.classList.remove('nav-hover');
      });
      li.classList.add('nav-hover');
      var id = li.getAttribute('data-mega-menu-trigger-id');
      if (id) bus.emit('mega-menu:show', { dropdownId: id, triggerId: id });
      lastShown = li;
      measureBridge();
    }

    // --- pointer tracking --------------------------------------------------------------

    function record(x, y) {
      var now = performance.now();
      history.push({ x: x, y: y, t: now });
      var cutoff = now - (SWITCH_DELAY * 3);
      while (history.length > 1 && history[0].t < cutoff) history.shift();
    }

    function latest() {
      return history.length ? history[history.length - 1] : null;
    }

    function travelSince(ms) {
      var p = latest();
      if (!p) return Infinity;
      var since = p.t - ms;
      var max = 0;
      for (var i = history.length - 1; i >= 0; i--) {
        var q = history[i];
        var d = Math.abs(q.x - p.x) + Math.abs(q.y - p.y);
        if (d > max) max = d;
        if (q.t < since) break;
      }
      return max;
    }

    function inBox(li, x, y) {
      // the heading link's own box; getBoundingClientRect excludes the ::after strip
      var a = linkOf(li);
      if (!a) return false;
      var r = a.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    }

    function overLinkBox(li) {
      var p = latest();
      return !!p && inBox(li, p.x, p.y);
    }

    function disarm() {
      if (timer) {
        clearTimeout(timer);
        clearInterval(timer);
        timer = null;
      }
      armed = null;
    }

    function arm(li) {
      disarm();
      armed = li;
      armedAt = performance.now();
      var open = openTrigger();
      if (open === li) return;

      if (!open) {
        timer = setTimeout(function () {
          timer = null;
          if (armed === li && isDesktop() && !openTrigger() && overLinkBox(li)) show(li);
        }, OPEN_DELAY);
        return;
      }

      // Another menu is open. Envy's hoverintent normally handles the switch; this is the
      // fallback for when (c) below has just put the previous menu back and hoverintent,
      // having already fired once for this heading, won't fire again until the pointer leaves.
      timer = setInterval(function () {
        if (armed !== li || !isDesktop()) { disarm(); return; }
        if (openTrigger() === li) { disarm(); return; }
        if (performance.now() - armedAt < SWITCH_DELAY) return;
        if (!overLinkBox(li)) return;
        if (travelSince(SWITCH_DELAY) >= SWITCH_TRAVEL) return;
        disarm();
        show(li);
      }, TICK);
    }

    nav.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      record(e.clientX, e.clientY);
    }, { passive: true });

    triggers.forEach(function (li) {
      li.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !isDesktop()) return;
        record(e.clientX, e.clientY);
        arm(li);
      });
      li.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        if (armed === li) disarm();
      });

      // e) a click on the strip, outside the heading text, does nothing
      var a = linkOf(li);
      if (a) {
        a.addEventListener('click', function (e) {
          if (!isDesktop() || e.detail === 0) return;
          if (!inBox(li, e.clientX, e.clientY)) e.preventDefault();
        });
      }
    });

    // --- f) New In and other plain links: settle on the text to close, else keep --------

    function armClose(li) {
      disarm();
      armed = li;
      armedAt = performance.now();
      timer = setInterval(function () {
        if (armed !== li || !isDesktop()) { disarm(); return; }
        if (!openTrigger()) { disarm(); return; }
        if (performance.now() - armedAt < SWITCH_DELAY) return;
        if (!overLinkBox(li)) return;
        if (travelSince(SWITCH_DELAY) >= SWITCH_TRAVEL) return;
        closeAll();
      }, TICK);
    }

    plain.forEach(function (li) {
      li.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !isDesktop()) return;
        record(e.clientX, e.clientY);
        if (openTrigger()) armClose(li);
      });
      li.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        if (armed === li) disarm();
      });
    });

    var leaveTimer = null;
    nav.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse' || !isDesktop()) return;
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(function () {
        if (lastShown && !onBar()) closeAll();
      }, 150);
    });

    // --- c) don't let the strip under a neighbour steal the open menu ------------------

    bus.on('mega-menu:show', function (data) {
      if (!data || !data.dropdownId) return;
      var li = triggerFor(data.dropdownId);
      if (!li) return;
      var prev = lastShown;
      lastShown = li;
      if (reverting || !isDesktop() || !prev || prev === li) return;
      // only when the pointer is physically in this neighbour's strip, not on its heading
      if (!hovered(li) || overLinkBox(li)) return;
      Promise.resolve().then(function () {
        if (lastShown !== li || overLinkBox(li) || !hovered(li)) return;
        reverting = true;
        show(prev);
        reverting = false;
        if (armed !== li) arm(li); // keep watching for the pointer settling on the heading
      });
    });

    // --- d) keep the open menu while the pointer is still on the bar -------------------

    bus.on('mega-menu:hide', function () {
      if (!isDesktop() || !lastShown) return;
      var keep = lastShown;
      Promise.resolve().then(function () {
        if (lastShown !== keep) return;                 // something else opened meanwhile
        if (openTrigger()) return;                      // already back, nothing to do
        if (hovered(keep) || onBar()) {
          show(keep);
        } else {
          lastShown = null;
        }
      });
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measureBridge, 200);
    });

    measureBridge();
    return true;
  }

  function boot() {
    if (init()) return;
    // theme.min.js is a deferred module; if the event bus isn't there yet, wait for it briefly
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      if (init() || tries > 40) clearInterval(poll);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
